import { onGetPdfBranding, onNormalizePdfAppearance } from "./PdfExportAppearance";

const CP1252 = new Map([
    ["€", 0x80], ["‚", 0x82], ["ƒ", 0x83], ["„", 0x84], ["…", 0x85],
    ["†", 0x86], ["‡", 0x87], ["ˆ", 0x88], ["‰", 0x89], ["Š", 0x8a],
    ["‹", 0x8b], ["Œ", 0x8c], ["Ž", 0x8e], ["‘", 0x91], ["’", 0x92],
    ["“", 0x93], ["”", 0x94], ["•", 0x95], ["–", 0x96], ["—", 0x97],
    ["˜", 0x98], ["™", 0x99], ["š", 0x9a], ["›", 0x9b], ["œ", 0x9c],
    ["ž", 0x9e], ["Ÿ", 0x9f],
]);

export const onPdfAnsi = (value) => {
    let output = "";
    for (const char of String(value ?? "")) {
        const code = char.codePointAt(0);
        if (code <= 0xff) output += String.fromCharCode(code);
        else if (CP1252.has(char)) output += String.fromCharCode(CP1252.get(char));
        else output += "?";
    }
    return output;
};

const onEscape = (value) => onPdfAnsi(value).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");

const onHexRgb = (hex, fallback = "#111111") => {
    const raw = /^#[0-9a-fA-F]{6}$/.test(String(hex || "")) ? String(hex) : fallback;
    return [1, 3, 5].map((start) => parseInt(raw.slice(start, start + 2), 16) / 255);
};

const onColor = (hex, mode = "fill") => `${onHexRgb(hex).map((value) => value.toFixed(3)).join(" ")} ${mode === "stroke" ? "RG" : "rg"}`;

const onWrapText = (value, maxChars = 80) => {
    const output = [];
    String(value ?? "").replace(/\r/g, "").split("\n").forEach((paragraph) => {
        if (!paragraph.trim()) { output.push(""); return; }
        let remaining = paragraph.trimEnd();
        while (remaining.length > maxChars) {
            let cut = remaining.lastIndexOf(" ", maxChars);
            if (cut < Math.floor(maxChars * 0.55)) cut = maxChars;
            output.push(remaining.slice(0, cut));
            remaining = remaining.slice(cut).trimStart();
        }
        output.push(remaining);
    });
    return output;
};

export const createPdfComposer = ({ appearance = {}, documentTitle = "Documento" } = {}) => {
    const config = onNormalizePdfAppearance(appearance);
    const brand = onGetPdfBranding();
    const pageWidth = 595;
    const pageHeight = 842;
    const marginX = config.density === "compact" ? 40 : 46;
    const bottomY = 58;
    const bodyTop = config.template === "professional" ? 785 : 790;
    const lineScale = config.density === "compact" ? 1.16 : 1.34;
    const accent = config.accent;
    const textColor = "#171717";
    const mutedColor = "#6b7280";
    const pages = [];
    let commands = [];
    let y = bodyTop;

    const pushHeader = () => {
        if (!config.showBranding) return;
        if (config.template === "professional") {
            commands.push(`${onColor(accent)} 0 810 ${pageWidth} 32 re f`);
            commands.push(`1 1 1 rg BT /F2 9 Tf ${marginX} 821 Td (${onEscape(brand.label)}) Tj ET`);
            return;
        }
        commands.push(`${onColor(mutedColor)} BT /${config.template === "technical" ? "F3" : "F2"} 8.5 Tf ${marginX} 819 Td (${onEscape(brand.label)}) Tj ET`);
        commands.push(`${onColor(accent, "stroke")} 0.7 w ${marginX} 808 m ${pageWidth - marginX} 808 l S`);
    };

    const startPage = () => {
        if (commands.length) pages.push(commands);
        commands = [];
        y = bodyTop;
        pushHeader();
    };

    pushHeader();

    const ensure = (height) => {
        if ((y - height) < bottomY) startPage();
    };

    const drawText = ({ text, x, atY, font = "F1", size = 9.5, color = textColor, underline = false, maxUnderlineWidth = null }) => {
        const safe = String(text ?? "");
        commands.push(`${onColor(color)} BT /${font} ${Number(size).toFixed(1)} Tf ${Number(x).toFixed(1)} ${Number(atY).toFixed(1)} Td (${onEscape(safe)}) Tj ET`);
        if (underline && safe) {
            const estimated = Math.min(maxUnderlineWidth || 9999, safe.length * size * (font === "F2" ? 0.56 : 0.51));
            commands.push(`${onColor(color, "stroke")} 0.75 w ${Number(x).toFixed(1)} ${(atY - 2).toFixed(1)} m ${(x + estimated).toFixed(1)} ${(atY - 2).toFixed(1)} l S`);
        }
    };

    const addText = ({ text = "", font = "regular", size = 9.5, indent = 0, before = 0, after = 3, lineHeight = null, color = textColor, underline = false, maxWidth = null } = {}) => {
        const fontId = font === "bold" ? "F2" : font === "mono" ? "F3" : "F1";
        y -= before;
        const step = lineHeight || Math.max(10.5, size * lineScale);
        if (!String(text).trim()) { y -= Math.max(after, step * 0.45); return; }
        const usable = maxWidth || (pageWidth - (marginX * 2) - indent);
        const avgChar = Math.max(4.1, size * (fontId === "F3" ? 0.60 : 0.52));
        const maxChars = Math.max(12, Math.floor(usable / avgChar));
        onWrapText(text, maxChars).forEach((line) => {
            ensure(step + 2);
            drawText({ text: line, x: marginX + indent, atY: y, font: fontId, size, color, underline, maxUnderlineWidth: usable });
            y -= step;
        });
        y -= after;
    };

    const addRule = ({ color = accent, before = 1, after = 8, width = 0.8 } = {}) => {
        y -= before;
        ensure(after + 4);
        commands.push(`${onColor(color, "stroke")} ${width} w ${marginX} ${y.toFixed(1)} m ${pageWidth - marginX} ${y.toFixed(1)} l S`);
        y -= after;
    };

    const addTitle = (text) => {
        addText({ text: text || "Documento", font: "bold", size: config.density === "compact" ? 19 : 22, color: accent, underline: true, after: 7 });
    };

    const addMeta = (text) => addText({ text, size: 8.6, color: mutedColor, after: 6 });

    const addSectionTitle = (text, { level = 1 } = {}) => {
        const size = level === 1 ? 12.5 : 11;
        addText({ text, font: "bold", size, color: level === 1 ? accent : textColor, underline: true, before: level === 1 ? 7 : 4, after: 5 });
    };

    const addLabelValue = (label, value, { labelWidth = null } = {}) => {
        const fontSize = config.density === "compact" ? 8.8 : 9.4;
        const step = fontSize * lineScale;
        const leftWidth = labelWidth || (config.density === "compact" ? 124 : 136);
        const gap = 10;
        const valueWidth = pageWidth - (marginX * 2) - leftWidth - gap;
        const labelChars = Math.max(8, Math.floor(leftWidth / (fontSize * 0.54)));
        const valueChars = Math.max(12, Math.floor(valueWidth / (fontSize * 0.52)));
        const labelLines = onWrapText(`${String(label || "Campo").replace(/:\s*$/, "")}:`, labelChars);
        const valueLines = onWrapText(String(value ?? "").trim() || "—", valueChars);
        const rows = Math.max(labelLines.length, valueLines.length, 1);
        ensure((rows * step) + 5);
        for (let index = 0; index < rows; index += 1) {
            if (labelLines[index] !== undefined) drawText({ text: labelLines[index], x: marginX, atY: y - (index * step), font: "F2", size: fontSize, color: textColor });
            if (valueLines[index] !== undefined) drawText({ text: valueLines[index], x: marginX + leftWidth + gap, atY: y - (index * step), font: config.template === "technical" ? "F3" : "F1", size: config.template === "technical" ? fontSize - 0.2 : fontSize, color: textColor });
        }
        y -= (rows * step) + (config.density === "compact" ? 2 : 4);
    };

    const addSpacer = (height = 6) => { y -= height; };

    const finish = () => {
        if (commands.length || !pages.length) pages.push(commands);
        const total = pages.length;
        const pageIds = pages.map((_, index) => 6 + (index * 2));
        const contentIds = pages.map((_, index) => 7 + (index * 2));
        const objectCount = 5 + (pages.length * 2);
        const objects = new Array(objectCount + 1);
        objects[1] = "<< /Type /Catalog /Pages 2 0 R >>";
        objects[2] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pages.length} >>`;
        objects[3] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>";
        objects[4] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>";
        objects[5] = "<< /Type /Font /Subtype /Type1 /BaseFont /Courier /Encoding /WinAnsiEncoding >>";

        pages.forEach((pageCommands, index) => {
            const footerParts = [];
            footerParts.push(`${onColor("#b7b7b7", "stroke")} 0.5 w ${marginX} 43 m ${pageWidth - marginX} 43 l S`);
            const leftFooter = config.showBranding ? brand.label : String(documentTitle || "Documento");
            const timestamp = config.showTimestamp ? ` · ${new Date().toLocaleString("es-AR")}` : "";
            const footerLabel = `${leftFooter}${timestamp}`.slice(0, 78);
            footerParts.push(`${onColor(mutedColor)} BT /F1 7.2 Tf ${marginX} 28 Td (${onEscape(footerLabel)}) Tj ET`);
            const pageText = `Pagina ${index + 1} de ${total}`;
            const pageX = Math.max(marginX, pageWidth - marginX - (pageText.length * 4.1));
            footerParts.push(`${onColor(mutedColor)} BT /F2 7.2 Tf ${pageX.toFixed(1)} 28 Td (${onEscape(pageText)}) Tj ET`);
            const content = [...pageCommands, ...footerParts].join("\n");
            const contentAnsi = onPdfAnsi(content);
            objects[pageIds[index]] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 3 0 R /F2 4 0 R /F3 5 0 R >> >> /Contents ${contentIds[index]} 0 R >>`;
            objects[contentIds[index]] = `<< /Length ${contentAnsi.length} >>\nstream\n${content}\nendstream`;
        });

        let pdf = "%PDF-1.4\n";
        const offsets = new Array(objectCount + 1).fill(0);
        for (let id = 1; id <= objectCount; id += 1) {
            offsets[id] = onPdfAnsi(pdf).length;
            pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`;
        }
        const xref = onPdfAnsi(pdf).length;
        pdf += `xref\n0 ${objectCount + 1}\n0000000000 65535 f \n`;
        for (let id = 1; id <= objectCount; id += 1) pdf += `${String(offsets[id]).padStart(10, "0")} 00000 n \n`;
        pdf += `trailer\n<< /Size ${objectCount + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;

        const binary = onPdfAnsi(pdf);
        const bytes = new Uint8Array(binary.length);
        for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index) & 0xff;
        return bytes;
    };

    return { config, brand, addText, addTitle, addMeta, addSectionTitle, addLabelValue, addRule, addSpacer, finish };
};
